import { useMemo } from 'react';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import ReviewStarFill from '@rover/icons/svg/review-star-fill.svg';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import type { ThemeSpacing } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';
import { useApiPersonDetailReviewsList } from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';

import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { getInitialReviewsParams, getYearsOfExperience } from '../../utils/utils';
import FeaturedExperienceHighlight from '../ExperienceHighlight/FeaturedExperienceHighlight';

import RatingBar, { type BarConfig, pageBar, RATING_LEVELS } from './RatingBar';

export type DistributionVariant = 'page' | 'gallerySm' | 'galleryLg' | 'review';

interface VariantConfig {
  headingSize: '400' | '500' | '600';
  starIconSize: number;
  ratingsTextSize: '200' | '300';
  contentPaddingLeft: ThemeSpacing | undefined;
  containerProps: (theme: ReturnType<typeof useTheme>) => Record<string, unknown>;
  showWhileLoading: boolean;
  bar: BarConfig;
}

const galleryBar: BarConfig = {
  listGap: '1x',
  listMarginTop: '3x',
  listPaddingRight: '4x',
  gap: '3x',
  borderRadius: '8px',
  cornerRadiusRight: undefined,
  trackBackground: (theme) => theme.colors.border.primary,
  labelFontWeight: 'semibold',
  barsDisplay: ['none', 'flex', 'flex', 'flex'],
};

const galleryContainerProps = (): Record<string, unknown> => ({
  p: '4x',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const galleryBase: VariantConfig = {
  headingSize: '400',
  starIconSize: 24,
  ratingsTextSize: '200',
  contentPaddingLeft: '5x',
  containerProps: galleryContainerProps,
  showWhileLoading: true,
  bar: galleryBar,
};

const pageConfig: VariantConfig = {
  headingSize: '600',
  starIconSize: 32,
  ratingsTextSize: '200',
  contentPaddingLeft: undefined,
  containerProps: (theme) => ({
    borderBottom: '1px solid',
    borderColor: theme.colors.separator.primary,
    pb: '8x',
    pt: '6x',
  }),
  showWhileLoading: false,
  bar: pageBar,
};

const VARIANT_CONFIG: Record<DistributionVariant, VariantConfig> = {
  page: pageConfig,
  gallerySm: galleryBase,
  galleryLg: { ...galleryBase, headingSize: '500', ratingsTextSize: '300' },
  review: {
    ...pageConfig,
    containerProps: () => ({
      borderBottom: '0px',
      pb: '6x',
      pt: '0x',
    }),
  },
};

interface Props {
  isModal?: boolean;
  variant?: DistributionVariant;
}

const RevampDistributionWidget = ({
  isModal = false,
  variant = 'page',
}: Props): JSX.Element | null => {
  const { t } = useLingui();
  const theme = useTheme();
  const { sitterData } = useRevampProfileContext();
  const initialReviewsParams = getInitialReviewsParams();

  const { data: reviews } = useApiPersonDetailReviewsList(sitterData.opk, {
    ...initialReviewsParams,
  });

  const distribution = reviews?.distribution;
  const totalRatings = useMemo(() => {
    return RATING_LEVELS.reduce((accumulator, level) => {
      return accumulator + (distribution?.[String(level)] ?? 0);
    }, 0);
  }, [distribution]);
  const totalTextReviews = reviews?.totalTextReviews ?? 0;

  const config = VARIANT_CONFIG[variant];
  const hasRatings = totalRatings > 0;

  if (!config.showWhileLoading && reviews === undefined) return null;

  const shouldNotAddPaddingBottom = variant === 'review' && !hasRatings && !isModal;
  const containerProps = shouldNotAddPaddingBottom
    ? { ...config.containerProps(theme), pb: '0x' as const }
    : config.containerProps(theme);

  const isInGallery = variant.startsWith('gallery');
  const showExperienceInsteadRatings =
    isInGallery && !hasRatings && getYearsOfExperience(sitterData) >= 1;

  if (showExperienceInsteadRatings) {
    return (
      <Box
        data-testid="revamp-distribution-widget"
        width="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        {...containerProps}
      >
        <Box pl={config.contentPaddingLeft}>
          <FeaturedExperienceHighlight sitter={sitterData} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="revamp-distribution-widget"
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent={!hasRatings ? 'center' : undefined}
      {...containerProps}
    >
      <Box pl={config.contentPaddingLeft}>
        <Flex alignItems="center" gap="2x">
          <Heading
            size={config.headingSize}
            mb="0x"
            webProps={{ responsive: false }}
            textColor="primary"
          >
            {hasRatings ? sitterData.ratingsAverage : '-'}
          </Heading>
          {hasRatings && <A11yHiddenBox>{t`stars`}</A11yHiddenBox>}
          <ReviewStarFill
            width={config.starIconSize}
            height={config.starIconSize}
            fill={theme.background.attention_highlight}
            aria-hidden="true"
          />
        </Flex>

        <Text size={config.ratingsTextSize} textColor="secondary">
          {t({
            message: plural(totalRatings, { one: '# rating', other: '# ratings' }),
          })}
          {isModal && (
            <>
              {' • '}
              {t({
                message: plural(totalTextReviews, { one: '# review', other: '# reviews' }),
              })}
            </>
          )}
        </Text>
      </Box>
      {hasRatings && (
        <Flex
          flexDirection="column"
          gap={config.bar.listGap}
          mt={config.bar.listMarginTop}
          pr={config.bar.listPaddingRight}
          display={config.bar.barsDisplay}
        >
          {RATING_LEVELS.map((level) => (
            <RatingBar
              key={`rating-bar-level-${level}`}
              rating={level}
              count={distribution?.[String(level)] ?? 0}
              total={totalRatings}
              bar={config.bar}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default RevampDistributionWidget;
